import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Produto {
  codigo_produto: string;
  nome: string;
  valor: number;
}

/**
 * O estado do fluxo de cotação — as duas telas (`/cotacao` e
 * `/cotacao-produtos`) conversam **só por aqui**.
 *
 * 🔴 **Nenhuma chave existente pode ser renomeada.** `/cotacao-produtos` lê o
 * slice **por nome** para montar o payload de `POST /cotacao-pdf/gerar`, e
 * `codigoRegionalSelecionada`/`codigoCooperativaSelecionada` são gravadas pelos
 * **modais** (efeito colateral legado que o `plan-c` §4.5 manda preservar
 * enquanto houver consumidor — e o consumidor é esta tela).
 *
 * 📌 As chaves acrescentadas na at-62 (D1) são **aditivas**, com valor vazio no
 * `initialState`: o `clearCotacaoData` (que devolve o `initialState`) as cobre
 * sozinho, e o redux-persist mescla chave nova sem migração — um `persist:root`
 * gravado antes desta branch continua válido.
 */
interface CotacaoState {
  nome: string;
  email: string;
  celular: string;
  cpf: string;
  placa: string;
  marca: string;
  modelo: string;
  ano: string;
  tipoVeiculo: string;
  tipoVeiculoDescricao: string;
  valorFipe: string;
  valorCota: string;
  valorRateio: number;
  codigoFipe: string;
  produtos: Produto[];
  valorCotaParticipacao: number | null;
  porcentagemFipe: number | null;
  codigoRegionalSelecionada: string;
  codigoCooperativaSelecionada: string;

  // ── at-62 (D1) ───────────────────────────────────────────────────────────
  /*
   * O que faltava para `/cotacao` **abrir preenchida** ao voltar de
   * `/cotacao-produtos`. Antes tudo isto vivia só no estado local da tela: quem
   * voltasse via o formulário em branco — mas a validação do submit lia regional
   * e cooperativa **do slice**, então a tela dizia "Nenhuma regional
   * selecionada" e o envio passava assim mesmo.
   */
  /** Nome da regional escolhida (o código vai em `codigoRegionalSelecionada`). */
  regionalNome: string;
  /** Nome da cooperativa escolhida (o código vai em `codigoCooperativaSelecionada`). */
  cooperativaNome: string;
  cotaId: string;
  cotaDescricao: string;
  /** Da cota escolhida — é o que recalcula a cota de participação num reenvio. */
  cotaPorcentagemFipe: number | null;
  /** Mês de referência da FIPE do modelo (at-21). Só exibição. */
  referenciaFipe: string;
  /**
   * Veículo 0km (at-84): o valor FIPE do modelo é o da tabela FIPE **0km**, não o
   * do usado do ano. Só muda a consulta FIPE — a cotação não grava veículo, então
   * não há placa a dispensar como na adesão. Não vai no payload do PDF.
   */
  zeroKm: boolean;

  /*
   * 🔴 O lead (`AssociadoCotacao`) já criado neste fluxo.
   *
   * Existe por causa do "Voltar" que a at-62 acrescentou: sem isto, cada reenvio
   * criaria **outro** lead e consumiria uma das 4 cotações do mês daquele CPF —
   * um consultor que voltasse só para corrigir o e-mail gastaria duas.
   *
   * ⚠️ `leadCpf` (dígitos) é o par obrigatório: o `PATCH` só vale enquanto o CPF
   * for o MESMO. Se ele mudou, quem está na tela é outra pessoa e o certo é um
   * lead novo — o limite é por CPF, então isso não consome nada do CPF novo.
   */
  leadId: string;
  leadCpf: string;
}

const initialState: CotacaoState = {
  nome: '',
  email: '',
  celular: '',
  cpf: '',
  placa: '',
  marca: '',
  modelo: '',
  ano: '',
  tipoVeiculo: '',
  tipoVeiculoDescricao: '',
  valorFipe: '',
  valorCota: '',
  valorRateio: 0,
  codigoFipe: '',
  produtos: [],
  valorCotaParticipacao: null,
  porcentagemFipe: null,
  codigoRegionalSelecionada: '',
  codigoCooperativaSelecionada: '',
  regionalNome: '',
  cooperativaNome: '',
  cotaId: '',
  cotaDescricao: '',
  cotaPorcentagemFipe: null,
  referenciaFipe: '',
  zeroKm: false,
  leadId: '',
  leadCpf: '',
};

const cotacaoSlice = createSlice({
  name: 'cotacao',
  initialState,
  reducers: {
    setCotacaoData: (state, action: PayloadAction<Partial<CotacaoState>>) => {
      Object.assign(state, action.payload);
    },
    setCodigoRegionalSelecionada: (state, action: PayloadAction<string>) => {
      state.codigoRegionalSelecionada = action.payload;
    },
    setCodigoCooperativaSelecionada: (state, action: PayloadAction<string>) => {
      state.codigoCooperativaSelecionada = action.payload;
    },
    clearCotacaoData: () => initialState,
  },
});

export const {
  setCotacaoData,
  setCodigoRegionalSelecionada,
  setCodigoCooperativaSelecionada,
  clearCotacaoData,
} = cotacaoSlice.actions;

export type { CotacaoState };

export default cotacaoSlice.reducer;
